import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { CheckCircle, Loader2, Clock } from 'lucide-react';
import api from '../../utils/api';

const MAX_ATTEMPTS = 8;

const BillingSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      setErrorMessage('No se encontro ID de sesion. Por favor, intenta nuevamente.');
      return;
    }
    if (sessionId.includes('{') || sessionId.includes('}')) {
      setStatus('error');
      setErrorMessage('ID de sesion invalido. Por favor, intenta crear una nueva suscripcion.');
      return;
    }
    setStatus('checking');
    setAttempts(0);
    setErrorMessage('');
  }, [sessionId]);

  useEffect(() => {
    if (status !== 'checking' || !sessionId) return;

    const checkPaymentStatus = async () => {
      if (attempts >= MAX_ATTEMPTS) {
        setStatus('timeout');
        setErrorMessage('Tiempo de espera agotado. El pago puede tardar unos minutos en procesarse.');
        return;
      }

      try {
        const response = await api.get(`/billing/checkout/status/${sessionId}`);
        if (response.data.payment_status === 'paid') {
          setStatus('success');
        } else if (response.data.status === 'expired') {
          setStatus('expired');
          setErrorMessage('La sesion de pago ha expirado.');
        } else {
          setTimeout(() => setAttempts((prev) => prev + 1), 2000);
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        setStatus('error');
        setErrorMessage(error.response?.data?.detail || 'Error al verificar el estado del pago.');
      }
    };

    checkPaymentStatus();
  }, [status, attempts, sessionId]);

  const handleRetry = () => {
    setStatus('checking');
    setAttempts(0);
    setErrorMessage('');
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" data-testid="billing-success-page">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          {status === 'checking' && (
            <>
              <Loader2 className="w-16 h-16 text-primary animate-spin mx-auto mb-4" />
              <CardTitle>Verificando pago...</CardTitle>
            </>
          )}
          {status === 'success' && (
            <>
              <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
              <CardTitle>Pago Exitoso!</CardTitle>
            </>
          )}
          {(status === 'error' || status === 'expired') && (
            <>
              <CardTitle className="text-destructive mb-2">Error al verificar el pago</CardTitle>
            </>
          )}
          {status === 'timeout' && (
            <>
              <Clock className="w-16 h-16 text-amber-500 mx-auto mb-4" />
              <CardTitle>Procesando pago...</CardTitle>
            </>
          )}
        </CardHeader>
        <CardContent className="text-center">
          {status === 'success' && (
            <>
              <p className="text-muted-foreground mb-6">Tu suscripcion ha sido activada correctamente.</p>
              <Button onClick={() => navigate('/client/dashboard')} className="w-full" data-testid="go-to-dashboard">
                Ir al Panel
              </Button>
            </>
          )}
          {(status === 'error' || status === 'expired') && (
            <>
              <p className="text-muted-foreground mb-4">{errorMessage}</p>
              <div className="flex flex-col gap-2">
                <Button onClick={() => navigate('/client/billing')} className="w-full">
                  Volver a Subscripcion
                </Button>
                <Button onClick={handleRetry} variant="outline" className="w-full">
                  Reintentar Verificacion
                </Button>
              </div>
            </>
          )}
          {status === 'timeout' && (
            <>
              <p className="text-muted-foreground mb-4">{errorMessage}</p>
              <div className="flex flex-col gap-2">
                <Button onClick={() => navigate('/client/dashboard')} className="w-full">
                  Ir al Panel
                </Button>
                <Button onClick={handleRetry} variant="outline" className="w-full">
                  Reintentar Verificacion
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BillingSuccess;
